/**
 * <StayWithTutorStep> — l'étape « Que préférez-vous ? » de « Garder son tuteur » (organism).
 *
 * Le tuteur ne peut plus assurer un ou plusieurs cours d'une matière. Ses créneaux sont des cartes
 * d'option à son visage, avec les places libres (« 2 places libres » — rien sur un cours
 * individuel) ; deux heures perdues se gardent en cochant deux créneaux, un pour chaque heure, et
 * le compte des créneaux cochés se lit au-dessus d'eux. Viennent ensuite, quand le serveur l'offre,
 * la troisième voie (garder le tuteur pour l'autre matière de son bloc), puis la carte qui garde
 * les créneaux avec un nouveau tuteur. La phrase d'en tête dit ce que fait le silence : sans
 * réponse avant l'échéance, c'est cette dernière carte qui s'applique.
 *
 * Contrôlé : le choix arrive en props, un créneau repart par `onToggleOption`, les deux autres
 * voies par `onSelect`. Pendant que les créneaux arrivent, leurs cartes montrent leur forme ; les
 * deux autres voies, qui ne dépendent pas d'eux, restent là.
 */
import React from "react"
import { Avatar } from "../../atoms/Avatar"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { OptionCard } from "../../molecules/OptionCard"
import { OptionCardSkeleton } from "../../molecules/OptionCard/OptionCardSkeleton"
import {
    type UITutor,
    type UITutorSlotMoveChoice,
    type UITutorSlotMoveOption,
    type UITutorSlotMovePick,
    type UITutorSlotMoveSplit,
} from "../../../ports/primary/ui/types"
import { formatShortDayTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { capitalizeFirst, countLabel, numberInWords } from "../../../ports/primary/ui/formatters/textFormatter"

/** Le visage du tuteur sur ses créneaux, et la pastille des deux autres voies. */
const LEADING_SIZE = 44

/** Un créneau, sans `pick` : le choix d'avant les choix par matière. */
const ONE: UITutorSlotMovePick = { min: 1, max: 1 }

export type StayWithTutorStepProps = {
    tutor: UITutor
    /** Le ou les créneaux libérés — « mardi 19h », « mardi 17h et mardi 18h ». */
    freedLabel: string
    /** Les heures perdues avec ce tuteur. Défaut 1. */
    hours?: number
    /** La fin de la fenêtre de choix (`YYYY-MM-DD HH:mm`). */
    deadline: string
    options: UITutorSlotMoveOption[]
    /** Combien de créneaux cocher. Défaut : un. */
    pick?: UITutorSlotMovePick
    /** « Garder le mardi 19h avec un nouveau tuteur ». */
    keepLabel: string
    /** La troisième voie — « Garder Yanis pour la physique, un nouveau tuteur pour les maths ». */
    split?: UITutorSlotMoveSplit | null
    selected?: UITutorSlotMoveChoice | null
    /** Un créneau du tuteur touché : la machine le coche, le décoche, ou le met à la place. */
    onToggleOption: (option: UITutorSlotMoveOption) => void
    /** « Garder avec un nouveau tuteur » ou la troisième voie. */
    onSelect: (choice: Exclude<UITutorSlotMoveChoice, { kind: "book" }>) => void
    loading?: boolean
    testID?: string
}

function optionDescription(tutor: UITutor, option: UITutorSlotMoveOption): string {
    const place = option.seatsLeft != null ? countLabel(option.seatsLeft, "place libre", "places libres") : null
    return [tutor.firstname ? `avec ${tutor.firstname}` : null, place].filter(Boolean).join(" · ")
}

/** « choisissez l'un de ses créneaux » · « deux de ses créneaux, un pour chaque heure » · « un ou deux ». */
function pickSentence(tutorName: string, pick: UITutorSlotMovePick): string {
    if (pick.max <= 1) return `Pour rester avec ${tutorName}, choisissez l'un de ses créneaux.`
    if (pick.min >= pick.max) {
        return `Pour rester avec ${tutorName}, choisissez ${numberInWords(pick.max)} de ses créneaux, un pour chaque heure.`
    }
    const range =
        pick.max === pick.min + 1
            ? `${numberInWords(pick.min)} ou ${numberInWords(pick.max)}`
            : `de ${numberInWords(pick.min)} à ${numberInWords(pick.max)}`
    return `Pour rester avec ${tutorName}, choisissez ${range} de ses créneaux. Chaque heure sans créneau choisi passe à un nouveau tuteur.`
}

export function StayWithTutorStep(props: StayWithTutorStepProps) {
    const {
        tutor,
        freedLabel,
        hours = 1,
        deadline,
        options,
        pick = ONE,
        keepLabel,
        split = null,
        selected,
        onToggleOption,
        onSelect,
        loading = false,
        testID,
    } = props
    const tutorName = tutor.firstname || "votre tuteur"
    const hasOptions = loading || options.length > 0
    const chosen = selected?.kind === "book" ? selected.options : []
    const atMax = pick.max > 1 && chosen.length >= pick.max

    const silence = `Sans réponse avant le ${formatShortDayTime(deadline)}, un nouveau tuteur reprend ${
        hours > 1 ? `les cours du ${freedLabel}` : `le ${freedLabel}`
    }.`
    // Sans créneau du tuteur, il n'y a rien à choisir chez lui : la phrase ne dit que le silence.
    const intro = (
        <Text size="text-sm" color="text-muted">
            {hasOptions ? `${pickSentence(tutorName, pick)} ${silence}` : silence}
        </Text>
    )

    let tutorBlock: React.ReactNode = null
    if (loading) {
        tutorBlock = (
            <DelayedSkeleton accessibilityLabel="Chargement des créneaux">
                <Stack gap="2">
                    <OptionCardSkeleton leadingSize={LEADING_SIZE} labelWidth={130} descriptionWidth={110} />
                    <OptionCardSkeleton leadingSize={LEADING_SIZE} labelWidth={110} descriptionWidth={140} />
                </Stack>
            </DelayedSkeleton>
        )
    } else if (options.length > 0) {
        tutorBlock = (
            <Stack gap="2">
                {pick.max > 1 && (
                    <Text size="text-sm" weight="semibold" color="text-muted">
                        {`${countLabel(chosen.length, "créneau choisi", "créneaux choisis")} sur ${pick.max}`}
                    </Text>
                )}
                {options.map((option) => {
                    const isChosen = chosen.some((candidate) => candidate.key === option.key)
                    return (
                        <OptionCard
                            key={option.key}
                            label={capitalizeFirst(option.label)}
                            description={optionDescription(tutor, option)}
                            selected={isChosen}
                            // Le compte est plein : un autre créneau attend qu'on en décoche un.
                            disabled={atMax && !isChosen}
                            onPress={() => onToggleOption(option)}
                            leading={
                                <Avatar uri={tutor.avatarUrl} name={tutorShortName(tutor)} size={LEADING_SIZE} signed />
                            }
                        />
                    )
                })}
            </Stack>
        )
    } else if (split == null) {
        tutorBlock = <Text color="text-muted">{`${capitalizeFirst(tutorName)} n'a plus de créneau libre.`}</Text>
    }

    return (
        <Stack gap="4" testID={testID}>
            {intro}
            {tutorBlock}
            {tutorBlock != null && <Divider label="ou" spacing="0" />}
            <Stack gap="2">
                {split != null && (
                    <OptionCard
                        label={split.label}
                        selected={selected?.kind === "split"}
                        onPress={() => onSelect({ kind: "split" })}
                        leading={
                            <IconBubble iconName="users" size={LEADING_SIZE} color="primary-soft" iconColor="primary" />
                        }
                    />
                )}
                <OptionCard
                    label={keepLabel}
                    selected={selected?.kind === "keep"}
                    onPress={() => onSelect({ kind: "keep" })}
                    leading={
                        <IconBubble iconName="user-plus" size={LEADING_SIZE} color="primary-soft" iconColor="primary" />
                    }
                />
            </Stack>
        </Stack>
    )
}
